"use client";

import React, { useState, useEffect } from "react";
import { Menu, Search } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { CartIcon } from "@/components/shop/CartIcon";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-6">
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-5 w-20 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-default font-medium tracking-wider text-foreground/80 hover:text-foreground transition-colors relative py-2 after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-[3px] after:bg-primary after:transition-all after:duration-300 hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  return <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-accent">
          <Menu className="size-6" />
          <span className="sr-only">Open Menu</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-screen h-[calc(100vh-5rem)] mt-5 rounded-md border-x-0 border-b-0 bg-background/95 backdrop-blur-xl p-6" sideOffset={0} align="start">
        {loading ? <div className="space-y-6 mt-4">
            {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-6 w-32 bg-muted/20 animate-pulse rounded-md" />)}
          </div> : <nav className="flex flex-col gap-6 mt-4">
            {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-xl font-default font-semibold text-foreground tracking-tight hover:text-primary transition-colors" onClick={() => setIsOpen(false)}>
                {item.label}
              </Link>)}
          </nav>}
      </PopoverContent>
    </Popover>;
}
function SearchAction() {
  const [searchValue, setSearchValue] = useState("");
  return <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent transition-colors">
          <Search className="size-5" />
          <span className="sr-only">Search</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3 bg-background/95 backdrop-blur-md border-border shadow-2xl rounded-md">
        <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search eco-packaging..." className="w-full" />
      </PopoverContent>
    </Popover>;
}
export default function StillOfficialPremiumHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section data-section-id="253092" id="sustainable-packaging-header" className={`sticky top-0 z-50 w-full transition-all duration-500 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-[16px] border-b border-border shadow-sm" : "bg-background border-b border-secondary"}`}>
      <style>{`
        @keyframes breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .animate-breathe {
          animation: breathe 4s ease-in-out infinite;
        }
      `}</style>
      
      <div className="container mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
        {}
        <div className="flex-1 flex items-center justify-start">
          <MenuProvider menu_id="22">
            <MobileMenu />
            <DesktopMenu />
          </MenuProvider>
        </div>

        {}
        <div className="flex-shrink-0 flex items-center justify-center">
          <Link to="/" className="relative group transition-transform duration-500 hover:scale-105">
            {}
            <div className="drop-shadow-sm group-hover:drop-shadow-md transition-all duration-500">
              <WvcLogo className="h-7 md:h-8 w-auto text-foreground" />
            </div>
          </Link>
        </div>

        {}
        <div className="flex-1 flex items-center justify-end gap-1 md:gap-3">
          <SearchAction />
          <div className="animate-breathe">
            <CartIcon variant="ghost" size="icon" className="text-foreground hover:bg-accent transition-colors" iconSize="size-5" />
          </div>
        </div>
      </div>
    </section>;
}